import { Component, OnInit } from '@angular/core';
import { BulkPriceStepComponent } from '@app/ps/project-settings/components/bulk-price/bulk-price-step/bulk-price-step.component';
import { OccupationModel } from '@app/ps/models/occupation.model';
import { Restangular } from 'ngx-restangular';
import { RoundingService } from '@app/ps/services/rounding.service';
import { OpinionService } from '@app/ps/services/opinion.service';
import { OpinionModel } from '@app/ps/models/opinion.model';
import { DecimalPipe } from '@app/common/pipes/decimal.pipe';

@Component({
  selector: 'bulk-price-result',
  templateUrl: './bulk-price-result.component.html',
})
export class BulkPriceResultComponent extends BulkPriceStepComponent implements OnInit {
  success = 0;
  fail = 0;

  constructor(
    private restangular: Restangular,
    private roundingService: RoundingService,
    private opinionService: OpinionService,
    private decimalPipe: DecimalPipe,
  ) {
    super();
    this.onSubmit = this.onSubmit.bind(this);
  }

  async ngOnInit() {
    if (this.data.occupationType === 'p') {
      this.processPurchase();

    } else {
      this.processRental();
    }
  }

  async processPurchase() {
    for (const otherSourcePricing of this.data.titleGroups) {
      const res = await this.uploadPurchase(otherSourcePricing);

      if (res) {
        this.success++;
      } else {
        this.fail++;
      }
    }

    const priceType = this.priceTypePurchase.find(i => i.id === this.data.purchasePrice.priceType).value;
    const price = `cena ${this.data.purchasePrice.price},- Kč${this.data.priceUnit === 'M' ? '/m2' : ''}`;

    this.restangular.all('notes/create').post({
      text: `Hromadné nastavení cen za Trvalý zábor, ${priceType}, ${price}. Cena nastavena u záborů, na které není navázaný aktuálně platný případ.`,
      projectInfo: true,
      systemType: false
    }).toPromise();
  }

  private uploadPurchase(titleGroup: any): Promise<boolean> {
    const opinion = {
      priceType: this.data.purchasePrice.priceType,
      otherPricingMethod: this.data.purchasePrice.priceUnit,
      occupationType: 'P',
      otherSource: this.data.purchasePrice.otherSource,
      title: titleGroup.title,
      parcelPrices: []
    };

    titleGroup.parcels.forEach(p => {
      let price;
      let squarePrice;

      if (this.data.purchasePrice.priceUnit === 'A') {
        price = this.decimalPipe.transform(this.data.purchasePrice.price);
        squarePrice = p.vymera ? price.div(p.vymera).toDP(5).toString() : null;
      } else {
        squarePrice = this.decimalPipe.transform(this.data.purchasePrice.price);
        price = p.vymera ? this.roundingService.roundProject(squarePrice.mul(p.vymera)) : null;
      }

      opinion.parcelPrices.push({
        otherLandPrice: price,
        otherLandSquarePrice: squarePrice,
        otherBuildingsPrice: 0,
        otherCompensationPrice: 0,
        otherVegetationPrice: 0,
        parcel: p
      });
    });

    return this.opinionService.createOne(opinion as OpinionModel)
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  async processRental() {
    for (const occupation of this.data.occupations) {
      const res = await this.uploadRental(occupation);

      if (res) {
        this.success++;
      } else {
        this.fail++;
      }
    }

    const occupationType = this.data.occupationType === 'u' ? 'Dočasný zábor do 1 roku' : 'Dočasný zábor nad 1 rok';
    const priceType = this.priceTypeRental.find(i => i.id === this.data.rentalPrice.rentalPriceType).value;
    let price = '';

    if (this.data.rentalPrice.rentalPriceType === 'O' && this.data.rentalPrice.rentalPriceUnit === 'T') {
      price = `jednorázová cena ${this.data.rentalPrice.totalRentPrice},- Kč`;
    } else if (!this.data.useAssessment) {
      price = `cena ${this.data.rentalPrice.priceAssessmentLandPrice},- Kč/m2`;
    } else {
      price = `cena podle výměru pro rok ${this.data.assessment.year}`;
    }

    this.restangular.all('notes/create').post({
      text: `Hromadné nastavení ceny za ${occupationType}, ${priceType}, ${price}. Cena nastavena u záborů, na které není navázaný aktuálně platný případ nájemní smlouvy.`,
      projectInfo: true,
      systemType: false
    }).toPromise();
  }

  onSubmit() {
    this.submitCallback.emit();
  }

  private uploadRental(occupation: OccupationModel): Promise<boolean> {
    Object.assign(occupation, this.data.rentalPrice);
    if (this.data.useAssessment && this.data.rentalPrice.rentalPriceType === 'F') {
        occupation.priceAssessmentLandPrice = this.data.assessment.price[occupation.parcel.katuzeKod];
        occupation.priceAssessmentYear = this.data.assessment.year;
        occupation.priceAssessmentPrice = { id: this.data.assessment.prices.find(p => p.areaId === occupation.parcel.katuzeKod).id };
    }
    return this.restangular.one('occupations', occupation.id)
      .customPUT(occupation)
      .toPromise()
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }
}
